import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dialog, IconButton, SimpleTable, StatGrid, StatTile } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { AiColumnType, AiDisplayDto } from "../../api/aiApi";

/**
 * What a tool hands back under the answer (ADR-0035) — like Turbo's AssistantArtifacts:
 * a table (first rows in the sheet, all rows in a dialog, CSV export, "save as report") or KPI tiles.
 */
type Row = Record<string, string | number | null>;

const ils = new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 2 });

export function formatCell(value: string | number | null | undefined, type?: AiColumnType): string {
  if (value === null || value === undefined || value === "") return "—";
  if (type === "money" && typeof value === "number") return ils.format(value);
  if (type === "number" && typeof value === "number") return value.toLocaleString("he-IL");
  if (type === "date" && typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10).split("-").reverse().join("/");
  return String(value);
}

/** CSV with a BOM so Excel opens Hebrew correctly. */
export function downloadCsv(display: Extract<AiDisplayDto, { kind: "table" }>) {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [
    display.columns.map((c) => esc(c.label)).join(","),
    ...display.rows.map((r) => display.columns.map((c) => esc(r[c.key] === null || r[c.key] === undefined ? "" : String(r[c.key]))).join(",")),
  ];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${display.title.replace(/[\\/:*?"<>|]+/g, " ").trim() || "טבלה"}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const PREVIEW_ROWS = 6;

function TableArtifact({
  display,
  onSave,
  onNavigate,
}: {
  display: Extract<AiDisplayDto, { kind: "table" }>;
  onSave?: (display: Extract<AiDisplayDto, { kind: "table" }>) => void;
  onNavigate?: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const columns = display.columns.map((c) => ({
    key: c.key,
    label: c.label,
    align: c.type === "money" || c.type === "number" ? ("end" as const) : undefined,
    render: (row: Row) => formatCell(row[c.key], c.type),
  }));
  const rows = display.rows.map((r, i) => ({ ...r, __key: String(i) }));
  const more = (display.total ?? display.rows.length) > PREVIEW_ROWS;
  const actions = (
    <>
      <IconButton icon="download" size="sm" label="ייצוא ל-CSV" onClick={() => downloadCsv(display)} />
      {onSave && display.source ? <IconButton icon="pin" size="sm" label="שמירה כדוח" onClick={() => onSave(display)} /> : null}
      <IconButton icon="expand" size="sm" label="הצגה מלאה" onClick={() => setOpen(true)} />
    </>
  );
  return (
    <div className="ai-art">
      <div className="ai-art__head">
        <div className="ai-art__titles">
          <b>{display.title}</b>
          {display.subtitle ? <small>{display.subtitle}</small> : null}
        </div>
        {actions}
      </div>
      <SimpleTable
        ariaLabel={display.title}
        columns={columns as never}
        rows={rows.slice(0, PREVIEW_ROWS)}
        rowKey="__key"
        empty="אין נתונים"
      />
      {more || display.path ? (
        <div className="ai-art__foot">
          {more ? (
            <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
              כל {(display.total ?? display.rows.length).toLocaleString("he-IL")} השורות
            </Button>
          ) : null}
          {display.path && onNavigate ? (
            <Button size="sm" variant="ghost" icon="left" onClick={() => onNavigate(display.path!)}>
              למסך המלא
            </Button>
          ) : null}
        </div>
      ) : null}
      <Dialog open={open} onClose={() => setOpen(false)} title={display.title} icon="list" size="lg">
        {display.subtitle ? <p className="ai-art__dialog-sub">{display.subtitle}</p> : null}
        <SimpleTable ariaLabel={display.title} columns={columns as never} rows={rows} rowKey="__key" maxHeight={560} empty="אין נתונים" />
        {display.total !== undefined && display.total > display.rows.length ? (
          <p className="ai-art__dialog-sub">מוצגות {display.rows.length.toLocaleString("he-IL")} מתוך {display.total.toLocaleString("he-IL")} — הרשימה המלאה במסך עצמו.</p>
        ) : null}
        <div className="ai-art__foot">
          <Button size="sm" icon="download" onClick={() => downloadCsv(display)}>
            ייצוא ל-CSV
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function KpiArtifact({ display }: { display: Extract<AiDisplayDto, { kind: "kpis" }> }) {
  return (
    <div className="ai-art">
      {display.title ? (
        <div className="ai-art__head">
          <div className="ai-art__titles">
            <b>{display.title}</b>
          </div>
        </div>
      ) : null}
      <StatGrid min={120}>
        {display.items.map((k) => (
          <StatTile
            key={k.label}
            label={k.label}
            tone={k.tone && k.tone !== "neutral" ? k.tone : undefined}
            value={typeof k.value === "number" ? formatCell(k.value, k.type === "money" ? "money" : "number") : k.value}
          />
        ))}
      </StatGrid>
    </div>
  );
}

export function AssistantArtifacts({
  displays,
  onSave,
}: {
  displays: AiDisplayDto[];
  onSave?: (display: Extract<AiDisplayDto, { kind: "table" }>) => void;
}) {
  const navigate = useNavigate();
  if (!displays.length) return null;
  return (
    <div className="ai-arts">
      {displays.map((d, i) =>
        d.kind === "table" ? (
          <TableArtifact key={i} display={d} onSave={onSave} onNavigate={(p) => navigate(p)} />
        ) : (
          <KpiArtifact key={i} display={d} />
        ),
      )}
    </div>
  );
}
